import { Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/react';
import { Modal, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { MenuItem } from '@components/menu';
import { useSnackbar } from '@components/snackbar';
// import { Input } from '@sketch-note/design-system';
import { ViewMode } from '@sketchnote/gantt-chart';
import {
  AutomationIcon,
  ColorToolIcon,
  CustomizeIcon,
  DeleteIcon,
  Epic2Icon,
  EyeHideIcon,
  FilterIcon,
  InfoIcon,
  MoveTaskIcon,
  SearchIcon,
  ViewIcon,
} from '@sketchnote/icons';
import { Input } from '@sketchnote/shared-components';
import { MoreActionMenu } from '@sketchnote/shared-components';
import Tippy from '@tippyjs/react';
import cn from 'classnames';
import debounce from 'lodash/debounce';
import { useRouter } from 'next/router';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useFileStore } from 'src/features/files';
import { useFileAccess } from 'src/features/files/hooks/useFileAccess';
import { useKanbanStore } from 'src/features/kanban';
import { MoveBoardItem } from 'src/features/kanban/component/Item';
import { SprintAnalytics } from 'src/features/scrum/components/sprint/SprintAnalytics';
import { SprintSelector } from 'src/features/scrum/components/sprint/SprintSelector';
import { useAgileStore } from 'src/features/scrum/store';
import { useAuth } from 'src/shared/hooks/use-auth';
import { useLayout } from 'src/shared/hooks/use-layout';

import { AutomationTabs } from '../../../components/file-explore/components/Automation/automation-tabs';
import { DeleteBoardItem } from '../../kanban/component/Item/DeleteItem';
import { DownloadTimesheet } from '../../timesheet/components/DownloadTimesheet';
import { useBoardStore } from '../store/useBoardStore';
import { BulkActions } from './BulkActions';
import { Filters } from './Filters';
import { QuickFilterAssignee, QuickFilterAssigneeProps } from './QuickFilter';
export const BoardHeader = ({
  boardId,
  type,
  isScrum,
}: {
  boardId: string;
  type: string;
  isScrum?: boolean;
}): JSX.Element => {
  if (['analytics', 'embed'].includes(type)) return <div />;

  const boardsStore = useBoardStore();
  const { splitView } = useLayout();
  const router = useRouter();
  const isAgile = router.query?.type === 'agile';

  const { isSticky } = useFileStore((s) => ({
    isSticky: s.isSticky,
  }));

  const selectedView = useAgileStore((s) => s.selectedView);

  const [isOpenGanttColorMode, setIsOpenGanttColorMode] = useState(false);
  const [isGanttViewOpen, setIsGanttViewOpen] = useState(false);
  const [showMoveFilesDialog, setShowMoveFilesDialog] = useState(false);
  const [showDeleteFilesDialog, setShowDeleteFilesDialog] = useState(false);
  const [isAutomateModalOpen, setIsAutomateModalOpen] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const isCustomizeOpen = useBoardStore((s) => s.calendarCustomize.isOpen);
  const onToggleCustomize = useBoardStore((s) => s.toggleCalendarCustomize);
  const selectedItems = useBoardStore((s) => s.selectedItems);
  const [searchValue, setSearchValue] = useState('');
  const [quickFilterAssignee, setQuickFilterAssignee] = useState('');
  const [openSnackbar] = useSnackbar();
  const userDetails = useAuth((s) => s.userDetails);

  const authData = useAuth((s) => s.userDetails);
  const currentFile = useFileStore(
    useCallback((s) => s.files[router.query.slug as string], [router.query.slug as string])
  );

  const { isReadOnly } = useFileAccess();

  const toggleFilter = (value?: boolean) => () => {
    setIsFilterOpen(value ?? !isFilterOpen);
  };

  useEffect(() => {
    const assigneeFilterOn = boardsStore?.filters.some((obj) => obj.filter === 'assignee');
    if (!assigneeFilterOn) setQuickFilterAssignee('');
  }, [boardsStore?.filters]);

  useEffect(() => {
    boardsStore.onChangeSearch(searchValue);
    debounceSearch(searchValue);
  }, [searchValue]);

  useEffect(() => {
    boardsStore.onChangeSearch(searchValue);
    if (!isAgile) debounceSearch(searchValue);
  }, [searchValue]);

  const debounceSearch = useCallback(
    debounce(async (searchStr: string) => {
      await handleOnSearch(searchStr);
    }, 2000),
    []
  );

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(event.target.value);
  };

  const handleOnSearch = async (searchStr: string) => {
    if (searchStr.length > 0) {
      boardsStore.actions.searchBoard({ id: boardId });
    } else {
      boardsStore.actions.fetchBoard(boardId);
    }
  };

  const assigneeOptions: QuickFilterAssigneeProps[] = React.useMemo(
    () =>
      currentFile?.membersWithAccess
        ? currentFile?.membersWithAccess?.map((obj) => ({
            name: obj.firstname,
            memberId: obj.memberId,
            avatar: obj.avatar,
          }))
        : [],
    [currentFile?.membersWithAccess]
  );

  useEffect(() => {
    if (currentFile?.membersWithAccess && currentFile?.membersWithAccess?.length > 0) {
      useKanbanStore.setState({ members: currentFile?.membersWithAccess });
    }
    userDetails &&
      useKanbanStore.setState({
        currentUser: {
          email: userDetails?.email,
          id: userDetails?.memberInformation?.memberId,
          avatar: userDetails?.avatarUrl,
          firstname: userDetails?.memberInformation?.firstname,
          lastname: userDetails?.memberInformation?.lastname,
        },
      });
  }, [currentFile?.membersWithAccess, userDetails]);
  const handleFilterClick = (filteredMemberId: string) => {
    setQuickFilterAssignee(filteredMemberId);
    boardsStore.onAddQuickFilter('assignee', filteredMemberId);
  };

  const handleResetFilters = () => {
    setQuickFilterAssignee('');
    boardsStore.onResetFilter();
  };

  const handleHideQuickFilter = async () => {
    boardsStore.onShowHideQuickFilter(false);
    openSnackbar({
      snackType: 'standard',
      text: 'Quick filters can be toggled from the filter dropdown',
      snackIcon: <InfoIcon wrapperClass="text-gray-500" />,
    });
  };

  const handleBulkActionMenuClick = (value: string) => {
    if (value === 'move') {
      setShowMoveFilesDialog(true);
    } else if (value === 'delete') {
      setShowDeleteFilesDialog(true);
    }
  };

  return (
    <div
      className={cn({
        'bg-white border-t border-gray-200 z-20 mobile:px-16 desktop:px-24 bigScreen:px-64': true,
      })}
    >
      <Modal
        isOpen={showMoveFilesDialog}
        onClose={() => setShowMoveFilesDialog(false)}
        motionPreset="none"
        isCentered
      >
        <ModalOverlay />
        <ModalContent minW={650} width={650} className="max-w-650 bg-white rounded-lg">
          <MoveBoardItem
            selectedItems={selectedItems}
            onCancel={() => setShowMoveFilesDialog(false)}
          />
        </ModalContent>
      </Modal>
      <Modal
        isOpen={showDeleteFilesDialog}
        onClose={() => setShowDeleteFilesDialog(false)}
        motionPreset="none"
        isCentered
      >
        <ModalOverlay />
        <ModalContent minW={650} width={650} className="max-w-650 bg-white rounded-lg">
          <DeleteBoardItem
            selectedItems={selectedItems}
            onCancel={() => setShowDeleteFilesDialog(false)}
          />
        </ModalContent>
      </Modal>
      <div
        className={cn('flex py-14 overflow-hidden', {
          'justify-between': router.query?.type !== 'timesheet',
          'justify-end': router.query?.type === 'timesheet',
        })}
      >
        {router.query.type !== 'timesheet' ? (
          <div
            className="flex -ml-4"
            style={{
              transition: 'all 300ms',
              transform: boardsStore.showQuickFilter ? '' : 'translateY(145%)',
            }}
          >
            {assigneeOptions.length > 0 ? (
              <QuickFilterAssignee
                assigneeOptions={assigneeOptions}
                quickFilterAssignee={quickFilterAssignee}
                setQuickFilterAssignee={setQuickFilterAssignee}
              />
            ) : null}

            {boardsStore.filters.length > 0 && (
              <button
                className={`text-sm text-gray-500 w-auto font-medium px-10 ${
                  quickFilterAssignee.length > 0 ? 'text-blue-500' : 'text-gray-500'
                }`}
                onClick={handleResetFilters}
              >
                Clear all
              </button>
            )}

            <button
              className={`text-sm w-auto font-medium pl-10 pr-10 ${
                quickFilterAssignee === authData?.memberInformation.memberId
                  ? 'text-blue-500'
                  : 'text-gray-500'
              }`}
              onClick={() => handleFilterClick(authData?.memberInformation.memberId as string)}
            >
              Only my tasks
            </button>

            <button
              className=" text-sm text-gray-500 w-auto font-medium px-10 border-l flex items-center"
              onClick={handleHideQuickFilter}
            >
              <EyeHideIcon />
              <span className="ml-8">Hide</span>
            </button>
          </div>
        ) : (
          <></>
        )}
        <div className="bg-white flex justify-end z-20">
          {router.query?.type === 'gantt' && (
            <div className="mr-15 flex items-center">
              <MoreActionMenu
                menuItems={[
                  // { label: 'Label', value: 'label' },
                  { label: 'Priority', value: 'priority' },
                  { label: 'Status column', value: 'status' },
                  { label: 'No color', value: 'none' },
                ]}
                onOpenMenu={() => {
                  setIsOpenGanttColorMode(true);
                }}
                onCloseMenu={() => {
                  setIsOpenGanttColorMode(false);
                }}
                onClickMenuItem={(e) => {
                  e.value && useBoardStore.setState({ ganttTaskColorMode: e.value as any });
                }}
                icon={
                  <span
                    className={cn('flex items-center text-sm', {
                      'text-gray-500': !isOpenGanttColorMode,
                    })}
                  >
                    <ColorToolIcon />
                    <span className="ml-6">{ganttColorMode[boardsStore.ganttTaskColorMode]}</span>
                  </span>
                }
              />
              <div className="mr-15" />
              <MoreActionMenu
                menuItems={[
                  { label: 'Hour', value: 'Hour' },
                  { label: 'Quarter Day', value: 'QuarterDay' },
                  { label: 'Half Day', value: 'HalfDay' },
                  { label: 'Day', value: 'Day' },
                  { label: 'Week', value: 'Week' },
                  { label: 'Month', value: 'Month' },
                ]}
                onOpenMenu={() => {
                  setIsGanttViewOpen(true);
                }}
                onCloseMenu={() => {
                  setIsGanttViewOpen(false);
                }}
                onClickMenuItem={(e) => {
                  e.value && useBoardStore.setState({ ganttView: ViewMode[e.value] });
                }}
                icon={
                  <span
                    className={cn('flex items-center text-sm', {
                      'text-gray-500': !isGanttViewOpen,
                    })}
                  >
                    <ViewIcon />
                    <span className="ml-6">{boardsStore.ganttView}</span>
                  </span>
                }
              />
            </div>
          )}
          {router.query?.view === 'calendar' && (
            <button
              className={cn('flex items-center justify-center text-sm mr-16', {
                'text-gray-500': !isCustomizeOpen,
              })}
              onClick={() => onToggleCustomize()}
            >
              <CustomizeIcon />
              <span className="ml-6">Customize</span>
            </button>
          )}
          {isScrum && (
            <>
              {!['sprints', 'epics'].includes(selectedView) ? (
                <>
                  {selectedView === 'board' ? (
                    <SprintSelector />
                  ) : (
                    <>
                      <SprintAnalytics />
                    </>
                  )}
                </>
              ) : (
                <></>
              )}
            </>
          )}

          {selectedItems.length > 0 && (
            <div className="flex items-center mr-8">
              <BulkActions
                onClickMenuItem={handleBulkActionMenuClick}
                selectedItems={selectedItems}
              />
            </div>
          )}

          {router.query?.type === 'kanban' && (
            <Tippy
              content="You do not have the required permission to edit automation"
              disabled={!isReadOnly}
            >
              <button
                className={cn('flex items-center mr-18', {
                  'text-gray-300': isReadOnly,
                  'text-primary-500': !isReadOnly,
                })}
                onClick={() => !isReadOnly && setIsAutomateModalOpen(true)}
              >
                <AutomationIcon fill={'currentColor'} />
                <span className="font-medium text-sm ml-4">Automation</span>
              </button>
            </Tippy>
          )}
          <AutomationTabs
            isAutomateModalOpen={isAutomateModalOpen}
            setIsAutomateModalOpen={setIsAutomateModalOpen}
          />
          {!splitView && router.query?.type !== 'timesheet' && (
            <div className="flex items-center">
              <Popover
                isOpen={isFilterOpen}
                onClose={toggleFilter}
                onOpen={toggleFilter}
                modifiers={[{ name: 'eventListeners', options: { scroll: isFilterOpen } }]}
              >
                <PopoverTrigger>
                  <button
                    className={cn('flex items-center justify-center text-sm', {
                      'text-gray-500': !isFilterOpen,
                    })}
                    onClick={toggleFilter()}
                  >
                    <FilterIcon />
                    <span className="ml-6">
                      Filter{' '}
                      {boardsStore.filters.length > 0 ? `(${boardsStore.filters.length})` : ''}
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent>
                  <Filters onClose={toggleFilter(false)} />
                </PopoverContent>
              </Popover>
            </div>
          )}
          {router.query?.type !== 'timesheet' && (
            <div className="flex w-143 h-28 items-center ml-16 py-16">
              <Input
                placeholder="Search..."
                inputClassName="placeholder-gray-500 font-regular"
                field="search"
                value={searchValue}
                leftIcon={<SearchIcon wrapperClass="align-text-bottom" />}
                wrapperClassName="py-2 bg-gray-100 border border-none rounded-md "
                onChange={handleOnChange}
              />
            </div>
          )}
          {router.query?.type === 'timesheet' && <DownloadTimesheet />}
        </div>
      </div>
    </div>
  );
};

const ganttColorMode = {
  label: 'Label',
  priority: 'Priority',
  status: 'Status column',
  none: 'No color',
};
